import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  Alert
} from '@mui/material';
import { usePiers } from '../context/PiersContext';

const HourlyDistributionView = () => {
  const { beltAssignments, configuration } = usePiers();

  const hourlyData = useMemo(() => {
    const hours = [];
    const beltHourlyCount = {};

    // Initialize hours from 00 to 23
    for (let h = 0; h < 24; h++) {
      hours.push(h.toString().padStart(2, '0'));
      beltHourlyCount[h.toString().padStart(2, '0')] = {};

      // Initialize belt counts for this hour
      for (let belt = 1; belt <= configuration.activeBelts; belt++) {
        beltHourlyCount[h.toString().padStart(2, '0')][belt] = 0;
      }
    }

    // Count flights per hour per belt
    Object.entries(beltAssignments).forEach(([belt, flights]) => {
      if (!flights || !Array.isArray(flights)) {
        return;
      }

      flights.forEach(flight => {
        const depTime = flight.depTime || flight.dep_time;
        if (!depTime) {
          return;
        }

        const hour = depTime.substring(0, 2);
        if (beltHourlyCount[hour] && beltHourlyCount[hour][parseInt(belt)] !== undefined) {
          beltHourlyCount[hour][parseInt(belt)]++;
        }
      });
    });

    // Calculate statistics for each hour
    const hourlyStats = hours.map(hour => {
      const beltCounts = Object.values(beltHourlyCount[hour]);
      const nonZeroCounts = beltCounts.filter(c => c > 0);
      const total = beltCounts.reduce((sum, count) => sum + count, 0);
      const max = Math.max(...beltCounts);
      const min = nonZeroCounts.length > 0 ? Math.min(...nonZeroCounts) : 0;
      const avg = nonZeroCounts.length > 0 ? total / nonZeroCounts.length : 0;
      const imbalance = max - min;

      return {
        hour,
        beltCounts: beltHourlyCount[hour],
        total,
        max,
        min,
        avg: avg.toFixed(1),
        imbalance,
        hasImbalance: imbalance >= 2 // Flag hours with significant imbalance
      };
    });

    // Filter out hours with no flights
    return hourlyStats.filter(stat => stat.total > 0);
  }, [beltAssignments, configuration.activeBelts]);

  // Calculate total flights per belt
  const beltTotals = useMemo(() => {
    const totals = {};
    for (let belt = 1; belt <= configuration.activeBelts; belt++) {
      totals[belt] = 0;
    }

    Object.entries(beltAssignments).forEach(([belt, flights]) => {
      if (flights && Array.isArray(flights)) {
        totals[parseInt(belt)] = flights.length;
      }
    });

    return totals;
  }, [beltAssignments, configuration.activeBelts]);

  // Calculate overall statistics
  const overallStats = useMemo(() => {
    const allImbalances = hourlyData.map(h => h.imbalance);
    const significantImbalances = allImbalances.filter(i => i >= 2);

    return {
      totalHours: hourlyData.length,
      imbalancedHours: significantImbalances.length,
      maxImbalance: Math.max(...allImbalances, 0),
      avgImbalance: allImbalances.length > 0
        ? (allImbalances.reduce((sum, i) => sum + i, 0) / allImbalances.length).toFixed(1)
        : 0
    };
  }, [hourlyData]);

  const getCellColor = (count, max, min) => {
    if (count === 0) return 'transparent';
    if (count === max && max - min >= 2) return 'rgba(255, 82, 82, 0.3)'; // Red for overloaded
    if (count === min && max - min >= 2) return 'rgba(76, 175, 80, 0.3)'; // Green for underloaded
    if (count > configuration.maxFlightsPerHour) return 'rgba(255, 152, 0, 0.3)'; // Orange for over limit
    return 'transparent';
  };

  const getCellTextColor = (count, max, min) => {
    if (count === 0) return '#666';
    if (count === max && max - min >= 2) return '#ff6b6b'; // Bright red
    if (count === min && max - min >= 2) return '#4caf50'; // Bright green
    if (count > configuration.maxFlightsPerHour) return '#ff9800'; // Orange
    return '#fff';
  };

  return (
    <Box sx={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {/* Summary Stats */}
      <Paper
        sx={{
          mb: 2,
          p: 2,
          bgcolor: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, color: '#F9B612', fontWeight: 600 }}>
          Hourly Distribution Summary
        </Typography>

        {overallStats.imbalancedHours > 0 ? (
          <Alert
            severity="warning"
            sx={{
              mb: 2,
              bgcolor: 'rgba(255, 152, 0, 0.1)',
              color: '#ff9800',
              border: '1px solid rgba(255, 152, 0, 0.3)',
              '& .MuiAlert-icon': {
                color: '#ff9800'
              }
            }}
          >
            Found {overallStats.imbalancedHours} hour{overallStats.imbalancedHours > 1 ? 's' : ''} with
            significant imbalance (≥2 flight difference between belts)
          </Alert>
        ) : (
          <Alert
            severity="success"
            sx={{
              mb: 2,
              bgcolor: 'rgba(76, 175, 80, 0.1)',
              color: '#4caf50',
              border: '1px solid rgba(76, 175, 80, 0.3)',
              '& .MuiAlert-icon': {
                color: '#4caf50'
              }
            }}
          >
            Good balance! No hours with significant imbalance detected.
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="body2" sx={{ color: '#999' }}>Active Hours</Typography>
            <Typography variant="h4" sx={{ color: '#fff' }}>{overallStats.totalHours}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" sx={{ color: '#999' }}>Imbalanced Hours</Typography>
            <Typography variant="h4" sx={{ color: overallStats.imbalancedHours > 0 ? '#ff9800' : '#4caf50' }}>
              {overallStats.imbalancedHours}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" sx={{ color: '#999' }}>Max Imbalance</Typography>
            <Typography variant="h4" sx={{ color: overallStats.maxImbalance >= 3 ? '#ff6b6b' : '#fff' }}>
              {overallStats.maxImbalance}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" sx={{ color: '#999' }}>Avg Imbalance</Typography>
            <Typography variant="h4" sx={{ color: '#fff' }}>{overallStats.avgImbalance}</Typography>
          </Box>
        </Box>

        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          <Chip
            label="Overloaded"
            size="small"
            sx={{ bgcolor: 'rgba(255, 82, 82, 0.3)', color: '#ff6b6b' }}
          />
          <Chip
            label="Underloaded"
            size="small"
            sx={{ bgcolor: 'rgba(76, 175, 80, 0.3)', color: '#4caf50' }}
          />
          <Chip
            label={`Over ${configuration.maxFlightsPerHour}/hour limit`}
            size="small"
            sx={{ bgcolor: 'rgba(255, 152, 0, 0.3)', color: '#ff9800' }}
          />
        </Box>
      </Paper>

      {/* Hourly Distribution Table */}
      <TableContainer
        component={Paper}
        sx={{
          flexGrow: 1,
          bgcolor: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '4px',
          },
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  bgcolor: 'rgba(0, 0, 0, 0.7)',
                  color: '#F9B612',
                  fontWeight: 600,
                  borderBottom: '2px solid rgba(255, 255, 255, 0.1)'
                }}
              >
                Hour
              </TableCell>
              {[...Array(configuration.activeBelts)].map((_, i) => (
                <TableCell
                  key={i + 1}
                  align="center"
                  sx={{
                    bgcolor: 'rgba(0, 0, 0, 0.7)',
                    color: '#F9B612',
                    fontWeight: 600,
                    borderBottom: '2px solid rgba(255, 255, 255, 0.1)',
                    minWidth: 80
                  }}
                >
                  <Box>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                      {configuration.beltNames?.[String(i + 1)] || `Belt ${i + 1}`}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                      ({beltTotals[i + 1] || 0})
                    </Typography>
                  </Box>
                </TableCell>
              ))}
              <TableCell
                align="center"
                sx={{
                  bgcolor: 'rgba(0, 0, 0, 0.7)',
                  color: '#F9B612',
                  fontWeight: 600,
                  borderBottom: '2px solid rgba(255, 255, 255, 0.1)'
                }}
              >
                Total
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  bgcolor: 'rgba(0, 0, 0, 0.7)',
                  color: '#F9B612',
                  fontWeight: 600,
                  borderBottom: '2px solid rgba(255, 255, 255, 0.1)'
                }}
              >
                Range
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  bgcolor: 'rgba(0, 0, 0, 0.7)',
                  color: '#F9B612',
                  fontWeight: 600,
                  borderBottom: '2px solid rgba(255, 255, 255, 0.1)'
                }}
              >
                Status
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {hourlyData.map((hourData) => (
              <TableRow
                key={hourData.hour}
                sx={{
                  bgcolor: hourData.hasImbalance ? 'rgba(255, 152, 0, 0.05)' : 'transparent',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.05)'
                  }
                }}
              >
                <TableCell
                  sx={{
                    color: '#fff',
                    fontWeight: 600,
                    borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                  }}
                >
                  {hourData.hour}:00
                </TableCell>
                {[...Array(configuration.activeBelts)].map((_, i) => {
                  const belt = i + 1;
                  const count = hourData.beltCounts[belt];
                  return (
                    <TableCell
                      key={belt}
                      align="center"
                      sx={{
                        bgcolor: getCellColor(count, hourData.max, hourData.min),
                        color: getCellTextColor(count, hourData.max, hourData.min),
                        fontWeight: count > 0 ? 600 : 400,
                        borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                      }}
                    >
                      {count > 0 ? (
                        <Tooltip title={`${count} flight${count > 1 ? 's' : ''}`}>
                          <span>{count}</span>
                        </Tooltip>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  );
                })}
                <TableCell
                  align="center"
                  sx={{
                    color: '#F9B612',
                    fontWeight: 600,
                    borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                  }}
                >
                  {hourData.total}
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    color: hourData.imbalance >= 2 ? '#ff9800' : '#999',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                  }}
                >
                  {hourData.min}-{hourData.max}
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                  }}
                >
                  {hourData.hasImbalance ? (
                    <Chip
                      label="Imbalanced"
                      size="small"
                      sx={{
                        bgcolor: 'rgba(255, 152, 0, 0.2)',
                        color: '#ff9800',
                        height: 20,
                        fontSize: '0.75rem'
                      }}
                    />
                  ) : (
                    <Chip
                      label="Balanced"
                      size="small"
                      sx={{
                        bgcolor: 'rgba(76, 175, 80, 0.2)',
                        color: '#4caf50',
                        height: 20,
                        fontSize: '0.75rem'
                      }}
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Quick Rebalance Tips */}
      {overallStats.imbalancedHours > 0 && (
        <Paper
          sx={{
            mt: 2,
            p: 2,
            bgcolor: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <Typography variant="subtitle2" sx={{ color: '#F9B612', mb: 1 }}>
            Rebalancing Suggestions:
          </Typography>
          <Typography variant="body2" sx={{ color: '#999' }}>
            • Review hours with red/green highlights for potential manual adjustments
            <br />
            • Consider moving flights from overloaded (red) to underloaded (green) belts
            <br />
            • Pay special attention to hours exceeding the {configuration.maxFlightsPerHour} flights/hour limit
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default HourlyDistributionView;